import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Atom, Zap, Waves, Telescope, Binary, FlaskConical } from 'lucide-react';
import './Home.css';

const researchAreas = [
  { icon: <Atom size={28} />, title: 'GRMHD Simulations', desc: 'State-of-the-art general relativistic magnetohydrodynamic simulations of accretion flows around black holes.' },
  { icon: <Zap size={28} />, title: 'Gamma-Ray Bursts', desc: 'Modeling GRB central engines, jet formation, propagation, and observable signatures in collapsars and mergers.' },
  { icon: <Waves size={28} />, title: 'Gravitational Waves', desc: 'Effects of magnetized accretion disks on EMRI gravitational wave signals detectable by LISA.' },
  { icon: <Telescope size={28} />, title: 'Kilonovae & r-Process', desc: 'Post-merger accretion disk winds, nucleosynthesis of heavy elements, and kilonova emission modeling.' },
  { icon: <Binary size={28} />, title: 'Black Hole Outflows', desc: 'Equatorial outflows and jets driven by large-scale magnetic fields in Kerr black hole spacetimes.' },
  { icon: <FlaskConical size={28} />, title: 'X-ray Variability', desc: 'Quasi-periodic eruptions and radiation-pressure instability cycles in accretion disks.' },
];

function Home() {
  const heroRef = useRef(null);
  const cardsRef = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    cardsRef.current.forEach(el => {
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <div className="home-page">
      {/* Hero */}
      <section className="home-hero" ref={heroRef}>
        <div className="hero-bg">
          <img src="/img/AstroGrupa_graphics/ryuYt0-1Ro4_sm.jpg" alt="" />
          <div className="hero-overlay" />
        </div>
        <div className="container hero-content">
          <div className="hero-badge">Center for Theoretical Physics · Polish Academy of Sciences</div>
          <h1>Astrophysics<br />Research Group</h1>
          <p className="hero-desc">
            Exploring the most energetic phenomena in the universe, from black hole accretion 
            and relativistic jets to gravitational waves and nucleosynthesis, through 
            numerical simulations.
          </p>
          <div className="hero-actions">
            <Link to="/research" className="btn btn-primary">
              Our Research <ArrowRight size={16} />
            </Link>
            <Link to="/publications" className="btn btn-ghost">
              Publications
            </Link>
          </div>
        </div>
      </section>

      {/* Research areas */}
      <section className="section home-research">
        <div className="container">
          <h2 className="section-title">Research Focus</h2>
          <p className="section-intro">
            We use general relativistic magnetohydrodynamics (GRMHD) and other computational methods 
            to study accretion processes, jet dynamics, and multi-messenger astrophysics around compact objects.
          </p>

          <div className="research-grid">
            {researchAreas.map((area, i) => (
              <div
                className="research-card fade-in"
                key={i}
                ref={el => cardsRef.current[i] = el}
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <div className="research-card-icon">{area.icon}</div>
                <h3>{area.title}</h3>
                <p>{area.desc}</p>
              </div>
            ))}
          </div>

          <div className="research-cta">
            <Link to="/research" className="btn btn-ghost">
              View detailed research projects <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Group leader */}
      <section className="section home-leader">
        <div className="container">
          <div className="leader-card">
            <div className="leader-image">
              <img src="/img/AstroGrupa_people/Agnieszka_sm.jpg" alt="Prof. Agnieszka Janiuk" />
            </div>
            <div className="leader-info">
              <div className="tag">Group Leader</div>
              <h3>Prof. Agnieszka Janiuk</h3>
              <p>
                Leading research in relativistic astrophysics, focusing on GRMHD simulations of accretion flows, 
                jet formation, and multi-messenger transients from compact object mergers and collapsars.
              </p>
              <Link to="/people" className="btn btn-ghost" style={{ marginTop: '1rem' }}>
                Meet the team <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Funding */}
      <section className="section home-funding">
        <div className="container">
          <h2 className="section-title">Funding</h2>
          <div className="funding-grid">
            <div className="funding-card">
              <div className="funding-label">NCN MAESTRO</div>
              <div className="funding-id">2023/50/A/ST9/00527</div>
              <p>Modeling astrophysical transients and multi-messenger signals from neutron star mergers and collapsars.</p>
            </div>
            <div className="funding-card">
              <div className="funding-label">NCN OPUS</div>
              <div className="funding-id">DEC-2019/35/B/ST9/04000</div>
              <p>Heavy element nucleosynthesis in neutrino-driven winds from gamma-ray burst engines.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Home;
